import { SKILL_MAP, SKILL_REGISTRY } from "../match/engine/content/skills"
import {
  computeSkillCostBreakdown,
  computeStatCost,
} from "../match/engine/costs"
import type { AuthoredCard, CardColor, SkillSelection } from "../schemas"

export type HeroLevelResult = {
  attack: number
  health: number
  skills: SkillSelection[]
}

// ---------------------------------------------------------------------------
// Cost distribution
// Targets a deck-valid spread: ~25% low (≤3), ~55% mid (4-6), ~20% high (≥7)
// ---------------------------------------------------------------------------

const COST_WEIGHTS: [cost: number, weight: number][] = [
  [1, 3],
  [2, 7],
  [3, 15],
  [4, 20],
  [5, 20],
  [6, 15],
  [7, 10],
  [8, 7],
  [9, 3],
]

function weightedRandomCost(): number {
  const total = COST_WEIGHTS.reduce((s, [, w]) => s + w, 0)
  let r = Math.random() * total
  for (const [cost, weight] of COST_WEIGHTS) {
    r -= weight
    if (r <= 0) return cost
  }
  return 9
}

// ---------------------------------------------------------------------------
// Stat generation
// ---------------------------------------------------------------------------

const ATTACK_A = 1.2
const HEALTH_A = 1.3

function expStatCost(x: number, a: number): number {
  if (x <= 0) return 0
  return (a ** x - 1) / (a ** 2 - 1)
}

function healthFromCost(targetCost: number): number {
  // Find the largest integer h ≥ 1 whose cost does not exceed targetCost.
  // Using a linear search avoids floating-point rounding in the log inverse,
  // which can otherwise return an h whose actual cost slightly exceeds the budget.
  let h = 1
  while (expStatCost(h + 1, HEALTH_A) <= targetCost + 1e-9 && h < 8) h++
  return h
}

function pickStats(statBudget: number): { attack: number; health: number } {
  const minHealthCost = expStatCost(1, HEALTH_A)

  let maxAttack = 0
  for (let a = 1; a <= 8; a++) {
    if (expStatCost(a, ATTACK_A) <= statBudget - minHealthCost) {
      maxAttack = a
    } else {
      break
    }
  }

  const attack = Math.floor(Math.random() * (maxAttack + 1))
  const healthBudget = statBudget - expStatCost(attack, ATTACK_A)
  const health = healthFromCost(healthBudget)

  return { attack, health }
}

// ---------------------------------------------------------------------------
// Skill selection — picks from the full registry, no pre-filter by color.
// Limits to at most 2 distinct non-colorless affinities so the result maps
// cleanly to a primary + optional secondary color.
// Pass allowedAffinities to constrain to specific color identities.
// Pass excludeIds to skip skills already selected (e.g. inherited from prev level).
// ---------------------------------------------------------------------------

function pickSkills(
  maxCount: number,
  budget: number,
  allowedAffinities?: Set<string>,
  excludeIds?: Set<string>,
): SkillSelection[] {
  if (maxCount === 0 || budget < 0.5) return []

  const eligible = [...SKILL_REGISTRY]
    .filter((s) => s.baseCostDelta > 0)
    .filter(
      (s) =>
        !allowedAffinities ||
        s.isColorless ||
        allowedAffinities.has(s.affinity),
    )
    .filter((s) => !excludeIds?.has(s.id))
    .sort(() => Math.random() - 0.5)

  const selected: SkillSelection[] = []
  let usedCost = 0
  const usedIds = new Set<string>()
  const usedAffinities = new Set<string>()

  for (const skill of eligible) {
    if (selected.length >= maxCount) break
    if (usedIds.has(skill.id)) continue
    if (usedCost + skill.baseCostDelta > budget + 0.25) continue

    // A third distinct non-colorless affinity would require a third color slot
    // which cards don't support, so skip it.
    if (
      !skill.isColorless &&
      usedAffinities.size >= 2 &&
      !usedAffinities.has(skill.affinity)
    )
      continue

    selected.push({ skillId: skill.id, mode: "default" })
    usedCost += skill.baseCostDelta
    usedIds.add(skill.id)
    if (!skill.isColorless) usedAffinities.add(skill.affinity)
  }

  return selected
}

// ---------------------------------------------------------------------------
// Derive card color from selected skills.
// Non-colorless skill affinities become the card's primary (and secondary)
// color. When no non-colorless skills are chosen, a random faction is assigned.
// ---------------------------------------------------------------------------

const FACTION_COLORS: CardColor[] = ["red", "yellow", "green", "blue", "purple"]

function randomFactionColor(): CardColor {
  return FACTION_COLORS[Math.floor(Math.random() * FACTION_COLORS.length)]
}

function deriveColors(skills: SkillSelection[]): {
  primaryColor: CardColor
  secondaryColor: CardColor | undefined
} {
  const affinities = [
    ...new Set(
      skills
        .map((s) => SKILL_MAP.get(s.skillId))
        .filter((def) => def != null && !def.isColorless)
        .map((def) => def?.affinity as CardColor),
    ),
  ]

  if (affinities.length === 0) {
    return { primaryColor: randomFactionColor(), secondaryColor: undefined }
  }

  if (affinities.length === 1) {
    return { primaryColor: affinities[0], secondaryColor: undefined }
  }

  // Two distinct affinities: randomly decide which is primary
  if (Math.random() < 0.5) {
    return { primaryColor: affinities[0], secondaryColor: affinities[1] }
  }
  return { primaryColor: affinities[1], secondaryColor: affinities[0] }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Hero level randomization
// Heroes have fixed cost caps per level: 3 / 6 / 10.
// Each level builds on the previous: inherits all skills (may add more),
// and stats are always ≥ the previous level's stats.
// ---------------------------------------------------------------------------

const HERO_LEVEL_CAPS = [3, 6, 10] as const
const HERO_MAX_SKILLS_PER_LEVEL = 3

// Skills that remove the unit from play — never valid on a hero.
export const HERO_FORBIDDEN_SKILL_IDS = new Set(
  SKILL_REGISTRY.filter((s) => s.scrapsUnit).map((s) => s.id),
)

/** Like pickStats but enforces minimums so each level is at least as strong. */
function pickStatsAtLeast(
  statBudget: number,
  minAttack: number,
  minHealth: number,
): { attack: number; health: number } {
  const minHealthCost = expStatCost(minHealth, HEALTH_A)

  // Find the max attack that leaves room for at least minHealth
  let maxAttack = minAttack
  for (let a = minAttack; a <= 8; a++) {
    if (expStatCost(a, ATTACK_A) <= statBudget - minHealthCost) {
      maxAttack = a
    } else {
      break
    }
  }

  // Random attack between min and max
  const attack =
    minAttack + Math.floor(Math.random() * (maxAttack - minAttack + 1))
  const healthBudget = statBudget - expStatCost(attack, ATTACK_A)
  // Max out health (fills the remaining budget rather than picking randomly)
  const health = Math.max(minHealth, healthFromCost(healthBudget))

  return { attack, health }
}

export function randomizeHeroLevels(): {
  levels: [HeroLevelResult, HeroLevelResult, HeroLevelResult]
  primaryColor: CardColor
  secondaryColor: CardColor | undefined
} {
  // Pick hero color identity first (50% chance of secondary color)
  const primaryColor = randomFactionColor()
  const secondaryColor =
    Math.random() < 0.5
      ? FACTION_COLORS.filter((c) => c !== primaryColor)[
          Math.floor(Math.random() * (FACTION_COLORS.length - 1))
        ]
      : undefined

  const allowedAffinities = new Set<string>(
    secondaryColor ? [primaryColor, secondaryColor] : [primaryColor],
  )

  // Build 3 levels progressively: each inherits previous skills and has ≥ stats
  let prevSkills: SkillSelection[] = []
  let prevAttack = 0
  let prevHealth = 1
  const builtLevels: HeroLevelResult[] = []

  for (let i = 0; i < 3; i++) {
    const cap = HERO_LEVEL_CAPS[i]

    // Estimate cost of inherited skills (at baseline statCost=0)
    const inheritedBreakdown = computeSkillCostBreakdown(
      prevSkills,
      SKILL_MAP,
      0,
    )
    const inheritedSkillCost = inheritedBreakdown.skillCostDelta

    // Try to add one more skill if there's budget and room under the cap.
    // Never pick skills that scrap the unit (heroes can't be self-scrapped).
    const slotsLeft = HERO_MAX_SKILLS_PER_LEVEL - prevSkills.length
    const extraSkillBudget = cap * 0.5 - inheritedSkillCost
    const excludedIds = new Set([
      ...prevSkills.map((s) => s.skillId),
      ...HERO_FORBIDDEN_SKILL_IDS,
    ])
    const extra =
      slotsLeft > 0
        ? pickSkills(1, extraSkillBudget, allowedAffinities, excludedIds)
        : []

    const skills = [...prevSkills, ...extra]

    // Re-estimate skill cost with added skills
    const estBreakdown = computeSkillCostBreakdown(skills, SKILL_MAP, 0)
    const statBudget = Math.max(0, cap - estBreakdown.skillCostDelta)

    let { attack, health } = pickStatsAtLeast(
      statBudget,
      prevAttack,
      prevHealth,
    )

    // Final accurate recompute and trim to cap
    let statCost = computeStatCost(attack, health)
    let finalBreakdown = computeSkillCostBreakdown(skills, SKILL_MAP, statCost)
    let derivedCost = Math.ceil(
      statCost +
        finalBreakdown.skillCostDelta +
        finalBreakdown.nonExhaustSurcharge +
        finalBreakdown.selfDestructSurcharge,
    )

    while (derivedCost > cap) {
      if (health > prevHealth) {
        health--
      } else if (attack > prevAttack) {
        attack--
      } else {
        break
      }
      statCost = computeStatCost(attack, health)
      finalBreakdown = computeSkillCostBreakdown(skills, SKILL_MAP, statCost)
      derivedCost = Math.ceil(
        statCost +
          finalBreakdown.skillCostDelta +
          finalBreakdown.nonExhaustSurcharge +
          finalBreakdown.selfDestructSurcharge,
      )
    }

    builtLevels.push({ attack, health, skills })
    prevSkills = skills
    prevAttack = attack
    prevHealth = health
  }

  return {
    levels: builtLevels as [HeroLevelResult, HeroLevelResult, HeroLevelResult],
    primaryColor,
    secondaryColor,
  }
}

export function randomizeCard(card: AuthoredCard): AuthoredCard {
  const targetCost = weightedRandomCost()

  // Pick 0-2 skills from the full registry; faction follows from the choices
  const maxSkills = Math.random() < 0.2 ? 0 : Math.random() < 0.5 ? 1 : 2
  const skillBudget = targetCost * 0.55
  const selectedSkills = pickSkills(maxSkills, skillBudget)

  const { primaryColor, secondaryColor } = deriveColors(selectedSkills)
  const secondaryColorSurcharge: 0 | 0.5 = secondaryColor ? 0.5 : 0

  // Estimate skill cost (baseline 0 is accurate for all skills except recycle)
  const estBreakdown = computeSkillCostBreakdown(selectedSkills, SKILL_MAP, 0)
  const approxSkillCost = estBreakdown.skillCostDelta

  const statBudget = Math.max(
    0,
    targetCost - secondaryColorSurcharge - approxSkillCost,
  )
  let { attack, health } = pickStats(statBudget)

  // Final accurate recompute with actual stat cost
  let statCost = computeStatCost(attack, health)
  let finalBreakdown = computeSkillCostBreakdown(
    selectedSkills,
    SKILL_MAP,
    statCost + secondaryColorSurcharge,
  )
  let derivedCost = Math.ceil(
    statCost +
      finalBreakdown.skillCostDelta +
      finalBreakdown.nonExhaustSurcharge +
      finalBreakdown.selfDestructSurcharge +
      secondaryColorSurcharge,
  )

  // Safety: trim health (then attack) if the ceiled total still exceeds the cap.
  // This handles residual floating-point edge cases after budget allocation.
  while (derivedCost > 9) {
    if (health > 1) {
      health--
    } else if (attack > 0) {
      attack--
    } else {
      break
    }
    statCost = computeStatCost(attack, health)
    finalBreakdown = computeSkillCostBreakdown(
      selectedSkills,
      SKILL_MAP,
      statCost + secondaryColorSurcharge,
    )
    derivedCost = Math.ceil(
      statCost +
        finalBreakdown.skillCostDelta +
        finalBreakdown.nonExhaustSurcharge +
        finalBreakdown.selfDestructSurcharge +
        secondaryColorSurcharge,
    )
  }

  return {
    ...card,
    primaryColor,
    secondaryColor,
    stats: { attack, health },
    selectedSkills,
    authoredCostInputs: {
      skillCostDelta: finalBreakdown.skillCostDelta,
      nonExhaustSurcharge: finalBreakdown.nonExhaustSurcharge,
      selfDestructSurcharge: finalBreakdown.selfDestructSurcharge,
      secondaryColorSurcharge,
    },
    derivedCost,
  }
}
